import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'

const CashbackAdd = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [cashbackValue, setCashbackValue] = useState('');
  const [maxCashback, setMaxCashback] = useState('');
  const [minSpend, setMinSpend] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [expireDate, setExpireDate] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isEditMode) {
      fetchCashbackData();
    }
  }, [id]);

  const fetchCashbackData = async () => {
    const token = localStorage.getItem('token');
    setIsLoading(true);
    setError(null);

    const myHeaders = new Headers();
    myHeaders.append("Authorization", `Bearer ${token}`);

    const requestOptions = {
      method: "GET",
      headers: myHeaders,
      redirect: "follow"
    };

    try {
      const response = await fetch(`https://api.experts4u.in/api/v1/cashback/${id}`, requestOptions);
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to fetch cashback data');
      }

      const data = result;
      setStartDate(data.startDate ? data.startDate.split('T')[0] : '');
      setEndDate(data.endDate ? data.endDate.split('T')[0] : '');
      setCashbackValue(data.cashbackValue ? data.cashbackValue.toString() : '');
      setMaxCashback(data.maxCashback ? data.maxCashback.toString() : '');
      setMinSpend(data.minimumSpend ? data.minimumSpend.toString() : '');
      setIsActive(data.isActive || false);
      setExpireDate(data.expireDate ? data.expireDate.split('T')[0] : '');
    } catch (error) {
      setError(error.message);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error fetching cashback data: ' + error.message
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    // Validate dates
    if (new Date(startDate) >= new Date(endDate)) {
      setError('End date must be after start date');
      setIsLoading(false);
      return;
    }

    // Validate required fields (matching backend validation)
    if (!startDate || !endDate || !cashbackValue || !maxCashback || !minSpend) {
      setError('All required fields must be filled');
      setIsLoading(false);
      return;
    }

    // Validate numeric values
    if (Number(cashbackValue) <= 0 || Number(maxCashback) <= 0 || Number(minSpend) <= 0) {
      setError('All numeric values must be greater than 0');
      setIsLoading(false);
      return;
    }

    const token = localStorage.getItem('token');
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("Authorization", `Bearer ${token}`);

    const formData = {
      startDate,
      endDate,
      cashbackValue: Number(cashbackValue),
      maxCashback: Number(maxCashback),
      minimumSpend: Number(minSpend),
      expireDate: expireDate,

      isActive
    };

    const requestOptions = {
      method: isEditMode ? "PUT" : "POST",
      headers: myHeaders,
      body: JSON.stringify(formData),
      redirect: "follow"
    };

    try {
      const url = isEditMode
        ? `https://api.experts4u.in/api/v1/cashback/${id}`
        : "https://api.experts4u.in/api/v1/cashback";

      const response = await fetch(url, requestOptions);
      const result = await response.json();

      if (!response.ok) {
        // Handle backend error format
        const errorMessage = result.error || result.message || `Failed to ${isEditMode ? 'update' : 'create'} cashback`;
        throw new Error(errorMessage);
      }

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: `Cashback ${isEditMode ? 'updated' : 'created'} successfully!`,
        showConfirmButton: false,
        timer: 1500
      }).then(() => {
        navigate('/cashback');
      });
    } catch (error) {
      setError(error.message);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: `Error ${isEditMode ? 'updating' : 'creating'} cashback: ` + error.message
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container-fluid py-3 py-md-5 mt-2 mt-md-4">
      <div className="row px-4">
        <div className="col-md-9">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title mb-4">{isEditMode ? 'Edit' : 'Add'} Cashback</h5>
              {error && <div className="alert alert-danger">{error}</div>}
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="mb-3 col-md-6">
                    <label className="form-label">Start Date:</label>
                    <input
                      type="date"
                      className="form-control"
                      value={startDate}
                      onChange={e => setStartDate(e.target.value)}
                      required
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                  <div className="mb-3 col-md-6">
                    <label className="form-label">End Date:</label>
                    <input
                      type="date"
                      className="form-control"
                      value={endDate}
                      onChange={e => setEndDate(e.target.value)}
                      required
                      min={startDate || new Date().toISOString().split('T')[0]}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="mb-3 col-md-6">
                    <label className="form-label">Cashback Value (%):</label>
                    <input
                      type="number"
                      className="form-control"
                      value={cashbackValue}
                      onChange={e => setCashbackValue(e.target.value)}
                      required
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div className="mb-3 col-md-6">
                    <label className="form-label">Max Cashback (₹):</label>
                    <input
                      type="number"
                      className="form-control"
                      value={maxCashback}
                      onChange={e => setMaxCashback(e.target.value)}
                      required
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="mb-3 col-md-6">
                    <label className="form-label">Minimum Spend (₹):</label>
                    <input
                      type="number"
                      className="form-control"
                      value={minSpend}
                      onChange={e => setMinSpend(e.target.value)}
                      required
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div className="mb-3 col-md-6">
                    {/* expire date */}
                    <label className="form-label">Expire Date:</label>
                    <input
                      type="date"
                      className="form-control"
                      value={expireDate}
                      onChange={e => setExpireDate(e.target.value)}
                    />
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          {/* <div className="card bg-light shadow-sm rounded-3 bg-white">
            <div className="card-body p-4">
              <h3 className="mb-4 fw-bold fs-2 text-dark">Publish</h3>
              <div className="mb-4">
                <label className="form-label fw-bold fs-4 text-dark">Status</label>
                <select
                  className="form-select form-select-lg border-secondary px-3 mx-3 py-2 rounded-3"
                  value={isActive ? "active" : "inactive"}
                  onChange={(e) => setIsActive(e.target.value === "active")}
                >
                  <option value="inactive">Inactive</option>
                  <option value="active">Active</option>
                </select>
              </div>
              <div className="d-flex justify-content-end">
                <button type="submit" className="btn btn-primary" disabled={isLoading} onClick={(e) => handleSubmit(e)} >
                  {isLoading ? 'Submitting...' : isEditMode ? 'Update' : 'Submit'}
                </button>
              </div>
            </div>
          </div> */}


          <div class="card"><div class="card-header"><h4 class="card-title mb-0">Publish</h4></div><div class="card-body"><div class="form-group"><strong>Status</strong><select class="form-control"
          value={isActive ? "active" : "inactive"}
          onChange={(e) => setIsActive(e.target.value === "active")}
          ><option value="">Select</option><option value="active">Active</option><option value="inactive">Inactive</option></select></div></div><div class="card-footer bg-secondary p-2">
            <div className="d-flex justify-content-end">
                <button type="submit" className="btn btn-primary" disabled={isLoading} onClick={(e) => handleSubmit(e)} >
                  {isLoading ? 'Submitting...' : isEditMode ? 'Update' : 'Submit'}
                </button>
              </div>
            </div><input type="hidden" id="action" name="action" value="coupon_new" /></div>
        </div>
      </div>
    </div>
  )
}

export default CashbackAdd